import {Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ChatLibraryService} from '../../chat-library/services/chat-library.service';
import {LibraryGroup} from '../../chat-library/models/chat-library.models';
import {AnchorChipComponent} from '../../chat-library/components/anchor-chip/anchor-chip.component';
import {DashboardService} from '../../../../services/dashboard.service';
import {ShareChatService} from '../share-chat-modal/share-chat.service';
import {ChatMessage, POLARIS_NAME, UserMessageResponse} from '../../copilot.model';
import {CommonModule} from '@angular/common';
import {ProgressBarComponent} from '../progress-bar/progress-bar.component';
import {ButtonComponent} from "../../../../shared/components/button/button.component";
import {MarkdownComponent} from '../../../../shared/components/markdown/markdown.component';
import {CopilotGraphsMessageComponent} from './copilot-graphs-message/copilot-graphs-message.component';
import {
  CopilotFollowupQuestionsMessageComponent
} from './copilot-followup-questions-message/copilot-followup-questions-message.component';
import {CopilotInsightsMessageComponent} from './copilot-insights-message/copilot-insights-message.component';
import {
  CopilotWebInsightsMessageComponent
} from './copilot-web-insights-message/copilot-web-insights-message.component';
import {CopilotCitationsMessageComponent} from './copilot-citations-message/copilot-citations-message.component';
import {TrimSurroundingQuotesPipe} from "../../../../pipes/trim-surrounding-quotes.pipe";
import {ResponseActionButtonsComponent} from '../response-action-buttons/response-action-buttons.component';
import {PolarisIconComponent} from '../polaris-icon/polaris-icon.component';
import {MESSAGE_APPEARANCE_DELAY, THINKING_DURATION} from '../../types';

export interface AnswerSegment {
  text: string;
  group: LibraryGroup | null;
  brandLogos: string[];
  loading: boolean;
}

@Component({
  selector: 'app-copilot-message',
  imports: [
    CommonModule,
    ProgressBarComponent,
    ButtonComponent,
    MarkdownComponent,
    CopilotGraphsMessageComponent,
    CopilotFollowupQuestionsMessageComponent,
    CopilotInsightsMessageComponent,
    CopilotWebInsightsMessageComponent,
    CopilotCitationsMessageComponent,
    TrimSurroundingQuotesPipe,
    ResponseActionButtonsComponent,
    PolarisIconComponent,
    AnchorChipComponent
  ],
  templateUrl: './copilot-message.component.html',
  styleUrl: './copilot-message.component.scss'
})
export class CopilotMessageComponent implements OnInit{
  private shareChatService = inject(ShareChatService);

  @Input() message!: ChatMessage;
  @Input() sendMessage!: string | null;
  @Input() shareable = false;
  @Input() shouldDisplayRetryButton = false;
  @Output() retryRequested = new EventEmitter<{ chatId: string; interactionId?: string }>();
  @Output() responseSelected = new EventEmitter<{ messageId: string; response: UserMessageResponse }>();
  @Output() chatNotFound = new EventEmitter<void>();
  public shouldDisplayMessage = false;
  public shouldDisplayThink = false;
  public isCopySucceeded = false;
  public polarisName = POLARIS_NAME;
  public activeHighlight: { start: number; end: number } | null = null;

  private readonly chatLibraryService = inject(ChatLibraryService);
  private readonly dashboardService = inject(DashboardService);
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  public readonly libraryState = this.chatLibraryService.state;
  private segments: AnswerSegment[] = [];
  private segmentsInputs: {
    content: string;
    groups: LibraryGroup[];
    highlight: { start: number; end: number } | null;
    brands: unknown;
    isLoading: boolean;
  } | null = null;

  constructor() {
    setTimeout(() => {
      this.shouldDisplayMessage = true;
      this.shouldDisplayThink = true;
    }, MESSAGE_APPEARANCE_DELAY);
    setTimeout(() => {
      this.shouldDisplayThink = false;
    }, MESSAGE_APPEARANCE_DELAY + THINKING_DURATION);
  }

  ngOnInit(): void {
    this.chatLibraryService.scrollToPassage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({passageId, start, end}) => {
        if (passageId !== this.message.id) return;
        this.highlightAndScrollTo(start, end);
      });
    this.chatLibraryService.scrollToGroup$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((groupId) => {
        const group = this.libraryState().groups.find(g => g.id === groupId);
        if (!group || group.answerPassageId !== this.message.id) return;
        this.highlightAndScrollTo(group.textLocation.start, group.textLocation.end, 300);
      });
  }

  private highlightAndScrollTo(start: number, end: number, delay = 0): void {
    this.activeHighlight = {start, end};
    setTimeout(() => {
      const target = this.el.nativeElement.querySelector(`.chat-highlight[data-message-id="${this.message.id}"]`)
        ?? this.el.nativeElement;
      target.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, delay);
  }

  getSegments(): AnswerSegment[] {
    const content = this.message.content;
    const groups = this.libraryState().groups;
    const highlight = this.activeHighlight;
    const brands = this.dashboardService.myBrandsAndCompetitors();
    const isLoading = this.libraryState().isLoading;
    const previous = this.segmentsInputs;
    if (!previous || previous.content !== content || previous.groups !== groups
      || previous.highlight !== highlight || previous.brands !== brands || previous.isLoading !== isLoading) {
      this.segments = this.buildSegments(content, groups, highlight, isLoading);
      this.segmentsInputs = {content, groups, highlight, brands, isLoading};
    }
    return this.segments;
  }

  trackBySegmentIndex(index: number): number {
    return index;
  }

  private buildSegments(
    content: string,
    allGroups: LibraryGroup[],
    highlight: { start: number; end: number } | null,
    isMetadataLoading: boolean
  ): AnswerSegment[] {
    const realGroups = allGroups.filter(group => group.answerPassageId === this.message.id);
    const loading = realGroups.length === 0 && isMetadataLoading;
    const groupsForMessage = loading ? this.buildPlaceholderGroups() : realGroups;

    const messageGroups = groupsForMessage
      .map(group => ({group, insertAt: this.clampInsertPosition(group.textLocation.end, content.length)}))
      .sort((a, b) => a.insertAt - b.insertAt);

    const segments: AnswerSegment[] = [];
    let cursor = 0;
    for (const {group, insertAt} of messageGroups) {
      segments.push({
        text: this.toDisplayText(content.slice(cursor, insertAt), cursor, highlight),
        group,
        brandLogos: loading ? [] : this.getGroupBrandLogos(group),
        loading,
      });
      cursor = insertAt;
    }
    segments.push({
      text: this.toDisplayText(content.slice(cursor), cursor, highlight),
      group: null,
      brandLogos: [],
      loading: false,
    });
    return segments;
  }

  private buildPlaceholderGroups(): LibraryGroup[] {
    return (this.message.chatLibrary?.groups ?? []).map(group => ({
      id: group.groupId,
      label: group.label,
      answerPassageId: this.message.id,
      textLocation: group.location,
      cards: [],
    }));
  }

  private clampInsertPosition(end: number, contentLength: number): number {
    if (!end || end <= 0) {
      return contentLength;
    }
    return Math.min(end, contentLength);
  }

  private toDisplayText(text: string, absoluteStart: number, highlight: { start: number; end: number } | null): string {
    const result = this.applyHighlight(text, absoluteStart, highlight);
    return this.replaceCreativesInMessage(result, this.message.creativesURLs || []);
  }

  public applyHighlight(text: string, absoluteStart: number, highlight: { start: number; end: number } | null): string {
    let result = text;
    if (highlight) {
      const start = Math.max(highlight.start - absoluteStart, 0);
      const end = Math.min(highlight.end - absoluteStart, text.length);
      if (start < end) {
        result = result.slice(0, start)
          + `<u class="chat-highlight" data-message-id="${this.message.id}">`
          + result.slice(start, end) + '</u>' + result.slice(end);
      }
    }
    return result;
  }

  private getGroupBrandLogos(group: LibraryGroup): string[] {
    const uniqueBrandKeys = [...new Set(group.cards.map(card => card.brandKey))];
    return uniqueBrandKeys
      .map(brandKey => this.dashboardService.getBrandIcon(brandKey))
      .filter(icon => !!icon);
  }

  public tryAgain() {
    this.emitRetryRequested();
  }

  public async onCopy(): Promise<void> {
    const success = await this.copyTextToClipboard();
    if (success) {
      this.isCopySucceeded = true;
      setTimeout(() => {
        this.isCopySucceeded = false;
      }, 2000);
    }
  }

  private async copyTextToClipboard(): Promise<boolean> {
    if (!this.message.content) {
      return false;
    }
    let text = this.message.content
      + `${this.message.insights ? '\nInsights:\n' + this.message.insights : ''}`
      + `${this.message.webInsights ? '\nWeb Insights:\n' + this.message.webInsights : ''}`;
    // Remove markdown formatting
    text = text
      .replace(/\*\*/g, '')  // Remove bold markers
      .replace(/\*/g, '')    // Remove italic markers
      .replace(/\_\_/g, '')  // Remove bold underscores
      .replace(/\_/g, '')    // Remove italic underscores
      .replace(/\~/g, '');   // Remove strikethrough

    await navigator.clipboard.writeText(text);
    return true;
  }

  public onShare(): void {
    if (this.sendMessage) {
      this.shareChatService.open(this.sendMessage);
    }
  }

  public onGoodResponse(): void {
    this.message.userResponse = UserMessageResponse.POSITIVE;
    this.responseSelected.emit({
      messageId: this.message.id,
      response: UserMessageResponse.POSITIVE
    });
  }

  public onBadResponse(): void {
    this.message.userResponse = UserMessageResponse.NEGATIVE;
    this.responseSelected.emit({
      messageId: this.message.id,
      response: UserMessageResponse.NEGATIVE
    });
  }

  public onRetry(): void {
    this.emitRetryRequested();
  }

  public replaceCreativesInMessage(message: string, creativesURLs: string[]): string {
    let newMessage = message;
    let currentLetter = 'A';
    creativesURLs.forEach(url => {
      const urlSplit = url.split('/');
      newMessage = newMessage.replace(urlSplit[urlSplit.length - 1], currentLetter);
      currentLetter = String.fromCharCode(currentLetter.charCodeAt(0) + 1);
    });
    return newMessage;
  }

  private emitRetryRequested(): void {
    if (!this.sendMessage) {
      return;
    }
    this.retryRequested.emit({
      chatId: this.sendMessage,
      interactionId: this.message.interactionId
    });
  }
}
